<template>
    <v-btn
        class="country-select-drawer no-print"
        aria-label="Country and Language slector selector"
        variant="flat"
        data-cy="preferences-button"
        @click="openPreferencesDrawer"
    >
        <country-flag
            v-if="sessionStore.countryCode"
            size="normal"
            :country="sessionStore.countryCode"
            :rounded="true"
        />

        <span class="country-code ml-1">
            {{ languageCode }}
        </span>
    </v-btn>

    <v-navigation-drawer
        v-model="isPreferencesDrawerOpen"
        width="500"
        tag="div"
        scrim
        temporary
        touchless
        :permanent="false"
        :disable-resize-watcher="true"
        :key="smAndUp ? 'right' : 'bottom'"
        :location="smAndUp ? 'right' : 'bottom'"
    >
        <div>
            <div class="d-flex align-center justify-space-between mb-8">
                <h2>{{ t("preferences.title") }}</h2>

                <v-btn
                    variant="text"
                    :icon="mdiClose"
                    class="close-button"
                    aria-label="Close country selector"
                    data-testid="core-modal-close"
                    @click="closePreferencesDrawer"
                />
            </div>

            <div :key="drawerResetKey">
                <language-selector v-model="selectedLanguage" class="mb-6" />

                <country-selector
                    v-model="selectedCountryCode"
                    cypress-data="country-selector"
                    input-name="country-selector"
                    input-id="country-selector"
                    label="labels.country"
                />

                <p class="mt-2">
                    {{ t("preferences.countryInformation") }}
                </p>
            </div>
        </div>

        <core-button
            colour="primary"
            class="mb-8"
            data-cy="confirm-preferences"
            @click="onConfirm"
        >
            {{ t("labels.confirm") }}
        </core-button>
    </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
    useCheckoutStore,
    useSessionStore,
    usePaymentsStore,
} from "@/plugins/store";
import { mdiClose } from "@mdi/js";
import CountryFlag from "vue-country-flag-next";
import countrySelector from "@/components/base/countrySelector.vue";
import languageSelector from "@/components/base/languageSelector.vue";
import { CoreButton } from "@esenda/core-ui";
import { useDisplay } from "vuetify";
import { useI18n } from "vue-i18n";
import { usePreferencesDrawer } from "@/composables/usePreferencesDrawer";

const sessionStore = useSessionStore();
const checkoutStore = useCheckoutStore();
const paymentsStore = usePaymentsStore();

const { smAndUp } = useDisplay();
const { locale, t } = useI18n();

const emit = defineEmits(["on-country-changed"]);

const {
    isPreferencesDrawerOpen,
    openPreferencesDrawer,
    closePreferencesDrawer,
} = usePreferencesDrawer();

const drawerResetKey = ref(0);

const selectedLanguage = ref<string>(sessionStore.language || "en-GB");
const selectedCountryCode = ref<string>(sessionStore.countryCode || "GB");

const languageCode = computed(
    () => sessionStore.language?.split("-")[0].toUpperCase() || "EN",
);

const onConfirm = () => {
    const countryIsValid = selectedCountryCode.value !== null;
    const hasCountryChanged =
        selectedCountryCode.value !== sessionStore.countryCode;

    sessionStore.updateLanguage(selectedLanguage.value);
    locale.value = selectedLanguage.value;

    if (countryIsValid && hasCountryChanged) {
        sessionStore.updateCountryCode(selectedCountryCode.value);
        checkoutStore.updateSupplementaryData({
            paymentOriginCountry: selectedCountryCode.value,
        });
        // Clear the stored address because it was entered for a different country.
        // Postal code format, state requirements, and the address countryCode field
        // are all country-specific, so the old address would be invalid or misleading
        // for the newly selected country.
        checkoutStore.clearPayerAddress();
        paymentsStore.setSelectedProvider(null);
        paymentsStore.setSelectedProviderDetails(null);
        emit("on-country-changed");
    }

    closePreferencesDrawer();
};

watch(isPreferencesDrawerOpen, (isOpen) => {
    if (isOpen) {
        drawerResetKey.value++;
    }
});
</script>

<style scoped>
.country-select-drawer {
    border: 0;
    border-radius: var(--radius-container);
    padding: 0 8px;
    height: 32px !important;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    line-height: 32px;
    display: flex;
}

p {
    font-size: 14px;
    font-weight: 600;
    text-align: left;
}

:deep(.flag) {
    margin: 0 -12px 0 -14px !important;
}

@media (max-width: 600px) {
    h2 {
        font-size: 16px;
        line-height: 24px;
    }
}
</style>

<style>
.v-navigation-drawer__scrim {
    height: 100vh;
}

.v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
    padding: 1rem;
}

@media (min-width: 600px) {
    .v-navigation-drawer__content {
        padding: 1rem 2rem;
    }
}
</style>
